import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { LoadingState } from '../ui/LoadingState';
import { MemoryMarkerDialog } from './MemoryMarkerDialog';
import { MemoryMarkerCard } from './MemoryMarkerCard';
import { PricelessPossessionsSection } from './PricelessPossessionsSection';
import {
  CONNECTED_MEMORIES_LABEL,
  deleteMemoryMarker,
  fetchMemoryMarkersForTree,
  filterNonPossessions,
  groupMarkersByType,
  MEMORY_MARKERS_UI_LABEL,
  partOfStoryLine,
} from '../../utils/memoryMarkers';
import { personHref } from '../../utils/prettyArchiveLinks';
import { toast } from 'sonner';
import { humanizeError } from '../../utils/errorHandling';

/**
 * Connected memories on a genealogical person profile.
 */
export function MemoryMarkersSection({
  treeId,
  personId,
  attachedPets = [],
  canEdit = false,
  anchorPerson = null,
  people = [],
  stories = [],
  onMarkersChange,
}) {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(Boolean(treeId && personId));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMarker, setEditingMarker] = useState(null);
  const [possessionsRefresh, setPossessionsRefresh] = useState(0);

  const loadMarkers = useCallback(async () => {
    if (!treeId || !personId) {
      setMarkers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const rows = await fetchMemoryMarkersForTree(treeId, { personId });
      setMarkers(rows);
    } catch {
      setMarkers([]);
    } finally {
      setLoading(false);
    }
  }, [treeId, personId]);

  useEffect(() => {
    loadMarkers();
  }, [loadMarkers]);

  const handleSaved = async () => {
    await loadMarkers();
    setPossessionsRefresh((n) => n + 1);
    onMarkersChange?.();
  };

  const handleDelete = async (marker) => {
    if (!window.confirm(`Remove "${marker.title}" from connected memories?`)) return;
    try {
      await deleteMemoryMarker(marker.id);
      toast.success('Connected memory removed.');
      await loadMarkers();
      onMarkersChange?.();
    } catch (err) {
      toast.error(humanizeError(err));
    }
  };

  const openCreate = () => {
    setEditingMarker(null);
    setDialogOpen(true);
  };

  const openEdit = (marker) => {
    setEditingMarker(marker);
    setDialogOpen(true);
  };

  const grouped = groupMarkersByType(filterNonPossessions(markers));
  const hasPlacesContent = filterNonPossessions(markers).length > 0 || attachedPets.length > 0;

  return (
    <>
      <Card className="border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)]/90" data-testid="memory-markers-section">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="font-serif text-lg">{MEMORY_MARKERS_UI_LABEL}</CardTitle>
              <CardDescription className="mt-1">
                {CONNECTED_MEMORIES_LABEL} — places, objects, and moments tied to this life.
              </CardDescription>
            </div>
            {canEdit ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 rounded-full border-[#8B7355]/30 text-[#5c4a32] hover:bg-[#8B7355]/8"
                onClick={openCreate}
                data-testid="add-connected-memory-btn"
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add a connected memory
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <PricelessPossessionsSection
            treeId={treeId}
            personId={personId}
            people={people.length ? people : [anchorPerson].filter(Boolean)}
            stories={stories}
            canEdit={canEdit}
            compact
            reloadToken={possessionsRefresh}
            onChange={onMarkersChange}
          />

          {loading ? <LoadingState message="Loading connected memories…" variant="minimal" /> : null}

          {!loading && attachedPets.length > 0 ? (
            <div>
              <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Family pets
              </p>
              <ul className="space-y-2">
                {attachedPets.map((pet) => (
                  <li key={pet.id}>
                    <Link
                      to={personHref(pet)}
                      className="flex items-center gap-2 rounded-lg border border-[hsl(38_14%_88%)]/80 bg-background/60 px-3 py-2 text-sm hover:border-[#8B7355]/30 hover:bg-[hsl(42_28%_100%)]"
                    >
                      <span className="font-medium">{pet.given_names}</span>
                      {pet.species ? (
                        <span className="text-xs capitalize text-muted-foreground">{pet.species}</span>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {!loading && grouped.length > 0
            ? grouped.map(({ type, label, items }) => (
                <div key={type}>
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {label}
                  </p>
                  <ul className="space-y-2">
                    {items.map((marker) => (
                      <MemoryMarkerCard
                        key={marker.id}
                        marker={marker}
                        canEdit={canEdit}
                        onEdit={openEdit}
                        onDelete={handleDelete}
                        anchorPerson={anchorPerson}
                        linkedStory={stories.find((s) => s.id === marker.linked_story_id)}
                        variant="profile"
                      />
                    ))}
                  </ul>
                </div>
              ))
            : null}

          {!loading && !hasPlacesContent ? (
            <div className="rounded-xl border border-dashed border-[hsl(38_14%_88%)]/80 bg-background/40 px-4 py-6 text-center">
              <p className="text-sm leading-relaxed text-muted-foreground">
                Homes, treasured objects, milestones, and family moments can live here — gentle anchors in the
                story, not chart positions on the family tree.
              </p>
              {canEdit ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-4 rounded-full"
                  onClick={openCreate}
                >
                  <Plus className="mr-1.5 h-4 w-4" />
                  Add a place, object, milestone or event
                </Button>
              ) : null}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {canEdit ? (
        <MemoryMarkerDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          treeId={treeId}
          marker={editingMarker}
          defaultPersonId={personId}
          stories={stories}
          onSaved={handleSaved}
        />
      ) : null}
    </>
  );
}

export function PetStoryAttachment({ pet, anchorPerson, className = '' }) {
  if (!pet?.attached_to_person_id || !anchorPerson) return null;
  const line = partOfStoryLine(anchorPerson);
  if (!line) return null;

  return (
    <p className={`text-sm text-[#5c4a32] ${className}`} data-testid="pet-story-attachment">
      {line} —{' '}
      <Link
        to={personHref(anchorPerson)}
        className="font-medium underline decoration-[#c9a961]/45 underline-offset-2 hover:text-[#1c1917]"
      >
        View {anchorPerson.given_names}&apos;s profile
      </Link>
    </p>
  );
}

export default MemoryMarkersSection;
